import { useEffect } from 'react';
import { Empty, theme, Typography } from 'antd';
import {
  Database,
  FileText,
  BarChart3,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import {
  type SubsystemDto,
  type SubsystemSectionTypeValue,
  SubsystemSectionType,
  SubsystemItemType,
} from '@api/subsystemsApi';

interface MegaMenuProps {
  acqSubsystem: SubsystemDto;
  /**
   * Резолвить ItemRef + ItemType у модуль-key для роутингу через MainLayout switch.
   * Якщо повертає null — пункт disabled.
   */
  acqResolveModuleKey: (acqItemType: number, acqItemRef: string) => string | null;
  acqOnClose: () => void;
  acqOnSelect: (acqModuleKey: string) => void;
}

const ACQ_SECTION_LABELS: Record<SubsystemSectionTypeValue, string> = {
  [SubsystemSectionType.Catalogs]: 'Довідники',
  [SubsystemSectionType.Documents]: 'Документи',
  [SubsystemSectionType.Reports]: 'Звіти',
  [SubsystemSectionType.DataProcessors]: 'Обробки',
};

const ACQ_SECTION_ICONS: Record<SubsystemSectionTypeValue, LucideIcon> = {
  [SubsystemSectionType.Catalogs]: Database,
  [SubsystemSectionType.Documents]: FileText,
  [SubsystemSectionType.Reports]: BarChart3,
  [SubsystemSectionType.DataProcessors]: Wrench,
};

/**
 * Спливне меню підсистеми (overlay поверх контенту, не зміщує дашборд).
 * 4 колонки: 3 для розділів (Documents/Catalogs/Reports) + 4-та для дочірніх підсистем
 * (якщо є). Esc / клік поза — закривають через batter handler у parent.
 */
export default function MegaMenu({
  acqSubsystem,
  acqResolveModuleKey,
  acqOnClose,
  acqOnSelect,
}: MegaMenuProps) {
  const { token: acqTheme } = theme.useToken();

  useEffect(() => {
    const acqHandle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') acqOnClose();
    };
    window.addEventListener('keydown', acqHandle);
    return () => window.removeEventListener('keydown', acqHandle);
  }, [acqOnClose]);

  // Збираємо items РЕКУРСИВНО з усього дерева (root + усі дочірні підсистеми) —
  // у 1С-стилі топ-підсистема показує контент усіх своїх дочірніх. Без цього у Продажах
  // нічого нема, бо документи лежать у ПродажиГлавное / РасчетыСКонтрагентами / ПДВ.
  const acqItemsByType = new Map<SubsystemSectionTypeValue, typeof acqSubsystem.sections[number]['items']>();
  const acqCollect = (acqSub: SubsystemDto) => {
    for (const acqSec of acqSub.sections) {
      const acqExisting = acqItemsByType.get(acqSec.type) ?? [];
      acqItemsByType.set(acqSec.type, [...acqExisting, ...acqSec.items]);
    }
    for (const acqCh of acqSub.children ?? []) acqCollect(acqCh);
  };
  acqCollect(acqSubsystem);

  // Дедуплікація по (itemType, itemRef) — щоб BAS-документ не дублювався, якщо лежить
  // одразу у root + у child.
  for (const [acqType, acqItems] of acqItemsByType) {
    const acqSeen = new Set<string>();
    const acqUnique = acqItems.filter((it) => {
      const acqKey = `${it.itemType}:${it.itemRef}`;
      if (acqSeen.has(acqKey)) return false;
      acqSeen.add(acqKey);
      return true;
    });
    acqItemsByType.set(acqType, acqUnique);
  }

  const acqOrder: SubsystemSectionTypeValue[] = [
    SubsystemSectionType.Documents,
    SubsystemSectionType.Catalogs,
    SubsystemSectionType.Reports,
  ];
  const acqColumns = acqOrder
    .filter((t) => (acqItemsByType.get(t)?.length ?? 0) > 0)
    .slice(0, 3);

  const acqHasChildSubsystems = (acqSubsystem.children?.length ?? 0) > 0;
  const acqColumnCount = acqColumns.length + (acqHasChildSubsystems ? 1 : 0);
  const acqIsEmpty = acqColumnCount === 0;

  return (
    <>
      {/* Затемнення-фон, ловить кліки поза мега-панеллю.
          top: 38 — висота CategoryNav, щоб затемнення не перекривало вкладок
          (інакше юзер не бачить меню для повернення/перемикання). */}
      <div
        onClick={acqOnClose}
        style={{
          position: 'absolute',
          top: 38,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(20, 20, 20, 0.04)',
          zIndex: 5,
        }}
      />

      {/* Сама мега-панель — top: 38 (висота CategoryNav) + 4 (gap). */}
      <div
        role="menu"
        aria-label={acqSubsystem.name}
        style={{
          position: 'absolute',
          top: 42,
          left: 8,
          right: 8,
          background: '#fff',
          border: `1px solid ${acqTheme.colorBorderSecondary}`,
          borderRadius: 12,
          padding: 0,
          zIndex: 10,
          display: 'grid',
          gridTemplateColumns: acqIsEmpty ? '1fr' : `repeat(${acqColumnCount}, 1fr)`,
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(20, 20, 20, 0.08)',
        }}
      >
        {acqIsEmpty ? (
          <div style={{ padding: 24 }}>
            <Empty description="У підсистемі поки немає розділів. Налаштуйте через Конструктор підсистем." />
          </div>
        ) : (
          <>
            {acqColumns.map((acqType, acqIdx) => {
              const AcqIcon = ACQ_SECTION_ICONS[acqType];
              const acqItems = acqItemsByType.get(acqType) ?? [];
              return (
                <div
                  key={acqType}
                  style={{
                    padding: '14px 14px 16px',
                    borderRight:
                      acqIdx < acqColumnCount - 1
                        ? `1px solid ${acqTheme.colorBorderSecondary}`
                        : 'none',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 8,
                      fontSize: 11,
                      letterSpacing: '0.5px',
                      color: '#8c8c8c',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                    }}
                  >
                    {AcqIcon && <span style={{ display: 'inline-flex' }}><AcqIcon size={12} /></span>}
                    {ACQ_SECTION_LABELS[acqType]}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {acqItems
                      .slice()
                      .sort((a, b) => a.orderIdx - b.orderIdx)
                      .slice(0, 30) // обмеження висоти — топ-30 у колонці
                      .map((acqIt) => {
                        const acqKey = acqResolveModuleKey(acqIt.itemType, acqIt.itemRef);
                        const acqDisabled = !acqKey;
                        const AcqItemIcon = acqIt.itemType === SubsystemItemType.Document
                          ? FileText
                          : acqIt.itemType === SubsystemItemType.Catalog
                            ? Database
                            : acqIt.itemType === SubsystemItemType.Report
                              ? BarChart3
                              : Wrench;
                        return (
                          <button
                            key={acqIt.id}
                            type="button"
                            disabled={acqDisabled}
                            onClick={() => {
                              if (acqKey) {
                                acqOnSelect(acqKey);
                                acqOnClose();
                              }
                            }}
                            style={{
                              display: 'flex',
                              gap: 10,
                              padding: '6px 8px',
                              borderRadius: 6,
                              cursor: acqDisabled ? 'not-allowed' : 'pointer',
                              alignItems: 'flex-start',
                              border: 'none',
                              background: 'transparent',
                              textAlign: 'left',
                              opacity: acqDisabled ? 0.4 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!acqDisabled) e.currentTarget.style.background = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ marginTop: 1, color: '#8c8c8c', flexShrink: 0 }}>
                              <AcqItemIcon size={14} />
                            </span>
                            <span style={{ fontSize: 13, lineHeight: 1.3 }}>
                              {acqIt.displayName ?? acqIt.itemRef}
                            </span>
                          </button>
                        );
                      })}
                    {acqItems.length > 30 && (
                      <Typography.Text type="secondary" style={{ fontSize: 11, paddingLeft: 8, marginTop: 4 }}>
                        +{acqItems.length - 30} ще (через пошук Ctrl+K)
                      </Typography.Text>
                    )}
                  </div>
                </div>
              );
            })}

            {acqHasChildSubsystems && (
              <div
                style={{
                  padding: '14px 14px 16px',
                  background: '#fafafa',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.5px',
                    color: '#8c8c8c',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    marginBottom: 8,
                  }}
                >
                  Підсистеми
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {acqSubsystem.children!.map((acqChild) => (
                    <div key={acqChild.id} style={{ padding: '6px 8px', fontSize: 13 }}>
                      {acqChild.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
